/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { featureCollection, point } from '@turf/helpers';
import type { Feature, FeatureCollection, Point } from 'geojson';

import { getMapIconName } from './getMapIconName';

import type { MapMarkerItem } from './map.types';

/**
 * Properties attached to each GeoJSON marker feature. Use this type when
 * reading feature properties from Mapbox query results or event handlers.
 * `asset` carries the consumer's original `MapMarkerItem` so click/popup
 * handlers can access the full record without a secondary lookup.
 */
export interface MapMarkerFeatureProperties<T extends MapMarkerItem> {
    id: string;
    entityType: string;
    icon: string;
    priority: number;
    asset: T;
    /** Empty string, `-active`, or `-disabled` — appended to the icon name. */
    suffix: '' | '-active' | '-disabled';
}

/**
 * A single GeoJSON point feature representing a map marker. Use this type
 * when handling Mapbox click/hover events — cast the event feature to
 * `MapMarkerFeature` to access typed `properties` (including `asset`).
 */
export type MapMarkerFeature<T extends MapMarkerItem> = Feature<
    Point,
    MapMarkerFeatureProperties<T>
>;

/**
 * Converts marker items to a GeoJSON FeatureCollection ready for a Mapbox
 * source. Items without both `latitude` and `longitude` are dropped.
 *
 * The `suffix` property drives the three-state icon system:
 * - `activeId` match → `-active`
 * - `availability === 'unavailable'` → `-disabled`
 * - otherwise → `''`
 *
 * `priority` is a descending index so earlier items render on top when
 * `symbol-sort-key` reads this field.
 */
export function convertToGeoJson<T extends MapMarkerItem>(
    data: readonly T[] | undefined,
    activeId?: string | null
): FeatureCollection<Point, MapMarkerFeatureProperties<T>> {
    const features: Array<MapMarkerFeature<T>> = [];

    if (!data || !data.length) return featureCollection(features);

    data.forEach((location, i, feats) => {
        const { longitude, latitude, entity_type, id } = location;

        if (!longitude || !latitude) return;

        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);

        const suffix: MapMarkerFeatureProperties<T>['suffix'] =
            activeId === id
                ? '-active'
                : location.availability === 'unavailable'
                  ? '-disabled'
                  : '';

        const properties: MapMarkerFeatureProperties<T> = {
            id,
            entityType: entity_type,
            icon: getMapIconName(location),
            priority: feats.length - i,
            asset: location,
            suffix,
        };

        features.push(point([lng, lat], properties));
    });

    return featureCollection(features);
}
